/**
 * Tracking events — the durable, proprietary moat layer (RFC-0004 Phase 1d).
 *
 * Every reviewer disposition on a per-program obligation is captured here, with
 * the reviewer (HITL, CI red line #5) and the *reason* ("+why"). This per-program
 * disposition history is the one defensible, in-customer, competitive asset a
 * generic agent cannot replicate (see docs/strategy/moat-design.md). Both event
 * types are APPEND-ONLY (mirrors the review log, ADR-0004): a correction/outcome
 * is never mutated; a later event supersedes an earlier one.
 *
 * Red line (ADR-0002): a CorrectionEvent records a human's judgement — it is not
 * a verdict the system asserts. No owner/dispatch fields (ADR-0002 §8).
 */

import { z } from "zod";

const Sha256 = z.string().regex(/^[a-f0-9]{64}$/);

/** Reviewer judgement on a surfaced change/signal: accept / dismiss / correct. */
export const CorrectionDisposition = z.enum(["accept", "dismiss", "correct"]);
export type CorrectionDisposition = z.infer<typeof CorrectionDisposition>;

export const CorrectionEventSchema = z.object({
  correctionEventId: z.string().min(1),
  programId: z.string().min(1),
  // The signal/source-change being dispositioned.
  signalId: z.string().min(1),
  ruleId: z.string().nullable(),
  disposition: CorrectionDisposition,
  // The "+why" — the durable signal that makes this proprietary.
  reason: z.string().min(1),
  reviewer: z.string().min(1), // HITL trail (CI red line #5)
  createdAt: z.string().min(1),
  // Provenance back to the changed source (optional — present for source changes).
  sourceUrl: z.string().url().nullable(),
  sha256: Sha256.nullable(),
});
export type CorrectionEvent = z.infer<typeof CorrectionEventSchema>;

/** Obligation lifecycle outcome on a program (what the OEM did about it). */
export const OutcomeKind = z.enum(["actioned", "closed", "reopened"]);
export type OutcomeKind = z.infer<typeof OutcomeKind>;

export const OutcomeEventSchema = z.object({
  outcomeEventId: z.string().min(1),
  programId: z.string().min(1),
  obligationItemId: z.string().min(1),
  outcome: OutcomeKind,
  note: z.string().nullable(),
  reviewer: z.string().min(1), // HITL trail (CI red line #5)
  createdAt: z.string().min(1),
});
export type OutcomeEvent = z.infer<typeof OutcomeEventSchema>;
